import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, ClipboardList, Loader2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import ModalShell from "./ModalShell";
import DateInput from "../../components/DateInput";

/**
 * Advance / revert modal — the classic "Move project to next stage" flow
 * with an optional note that gets included in the notification email.
 * Also supports reverting to an earlier stage (audited but allowed).
 */
export function AdvanceModal({ project, data, statusMeta, onClose, onDone }) {
  const cur = project.lifecycle_status || "solar_request";
  const curMeta = statusMeta[cur];
  const options = data.statuses.filter((s, i) => i > (curMeta?.index ?? 0));
  const revertOptions = data.statuses.filter((s, i) => i < (curMeta?.index ?? 0));
  const defaultTarget = curMeta?.next || options[0]?.key || cur;
  const [target, setTarget] = useState(defaultTarget);
  const [note, setNote] = useState("");
  const [handoverDate, setHandoverDate] = useState(project?.handover_date || "");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    // Handover-date guard — moving to Final Handover requires a date.
    // Prompt for it inline; we submit both to the lifecycle endpoint in
    // the same call so it's atomic.
    if (target === "final_handover" && !project.handover_date && !handoverDate) {
      toast.error("Enter a handover date first.");
      return;
    }
    setSaving(true);
    try {
      const { data: res } = await api.patch(`/projects/${project.id}/lifecycle`, {
        to_stage: target,
        note: note || undefined,
        ...(target === "final_handover" && handoverDate ? { handover_date: handoverDate } : {}),
      });
      toast.success(`Moved to ${statusMeta[res.lifecycle_status]?.label || res.lifecycle_status}. Notifications sent.`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not advance stage");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell onClose={onClose} testid="pm-advance-modal">
      <div className="p-5 border-b border-[var(--line)]">
        <div className="label mb-1">Lifecycle stage</div>
        <div className="font-display text-xl font-bold">{project.name}</div>
        <div className="muted text-xs mt-1">Currently in <strong>{curMeta?.label}</strong></div>
      </div>
      <div className="p-5 space-y-4">
        <div>
          <label className="label mb-1.5 block">Move to</label>
          <select
            value={target} onChange={(e) => setTarget(e.target.value)}
            className="input-box w-full"
            data-testid="pm-advance-select"
          >
            {options.length > 0 && (
              <optgroup label="Advance to">
                {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </optgroup>
            )}
            {revertOptions.length > 0 && (
              <optgroup label="Revert to (audited)">
                {revertOptions.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </optgroup>
            )}
          </select>
        </div>
        <div>
          <label className="label mb-1.5 block">Note (optional — included in email)</label>
          <textarea
            value={note} onChange={(e) => setNote(e.target.value)}
            rows={3}
            className="input-box w-full text-sm"
            placeholder="Any context for the next-stage owner…"
            data-testid="pm-advance-note"
          />
        </div>
        {target === "final_handover" && (
          <div data-testid="pm-advance-handover-date">
            <label className="label mb-1.5 block">
              Handover date <span className="text-red-600">*</span>
            </label>
            <DateInput
              className="input-box w-full text-sm"
              value={handoverDate}
              onChange={(e) => setHandoverDate(e.target.value)}
              data-testid="pm-advance-handover-input"
            />
            <div className="text-[10px] muted mt-1">
              Required — captures when the project actually completed for planned-vs-actual tracking.
            </div>
          </div>
        )}
        <div className="text-[11px] muted flex items-start gap-1.5">
          <ClipboardList size={12} className="mt-0.5" />
          <span>The project owner and the next-stage assignee will receive an email.</span>
        </div>
      </div>
      <div className="p-4 border-t border-[var(--line)] flex justify-end gap-2 bg-[var(--surface-2,#F6F7F9)]">
        <button className="btn-ghost" onClick={onClose} data-testid="pm-advance-cancel">Cancel</button>
        <button
          className="btn-accent" onClick={submit} disabled={saving || !target || target === cur}
          data-testid="pm-advance-submit"
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <ArrowRight size={12} />}
          Confirm
        </button>
      </div>
    </ModalShell>
  );
}


/**
 * Assignees modal — set the user assigned to each PM role KEY on this
 * project. A user can be PM here and EPC on another project (assignments
 * are per-project, not global).
 */
export function AssigneesModal({ project, data, users, onClose, onDone }) {
  const [values, setValues] = useState(() => {
    const out = {};
    for (const rk of data.role_keys) {
      out[rk.key] = (project.pm_assignees || {})[rk.key] || "";
    }
    return out;
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const [k, v] of Object.entries(values)) payload[k] = v || null;
      await api.patch(`/projects/${project.id}/pm-assignees`, { assignees: payload });
      toast.success("PM assignees updated");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save assignees");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell onClose={onClose} testid="pm-assignees-modal">
      <div className="p-5 border-b border-[var(--line)]">
        <div className="label mb-1">PM assignees</div>
        <div className="font-display text-xl font-bold">{project.name}</div>
        <div className="muted text-xs mt-1">
          A user can be a Project Manager here and an EPC Contractor on another site — assignments are per-project.
        </div>
      </div>
      <div className="p-5 space-y-4">
        {data.role_keys.map((rk) => {
          // The "asset_manager" slot must only accept dedicated AMs
          // (role="asset_manager") plus admins/managers as sensible
          // fallbacks. Other role slots (pm/epc/finance) stay open.
          const options = rk.key === "asset_manager"
            ? users.filter((u) => ["asset_manager", "admin", "manager"].includes(u.role))
            : users;
          return (
            <div key={rk.key}>
              <label className="label mb-1.5 block">{rk.label}</label>
              <select
                className="input-box w-full"
                value={values[rk.key]}
                onChange={(e) => setValues((v) => ({ ...v, [rk.key]: e.target.value }))}
                data-testid={`pm-assignee-${rk.key}`}
              >
                <option value="">— unassigned —</option>
                {options.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name || u.email} ({u.role})
                  </option>
                ))}
              </select>
              {rk.key === "asset_manager" && options.length === 0 && (
                <div className="text-[10px] muted mt-1">
                  No Asset Managers yet — add one under Settings › Asset Managers.
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="p-4 border-t border-[var(--line)] flex justify-end gap-2 bg-[var(--surface-2,#F6F7F9)]">
        <button className="btn-ghost" onClick={onClose} data-testid="pm-assignees-cancel">Cancel</button>
        <button className="btn-accent" onClick={submit} disabled={saving} data-testid="pm-assignees-save">
          {saving ? <Loader2 size={12} className="animate-spin" /> : null} Save
        </button>
      </div>
    </ModalShell>
  );
}
